/**
 * taskflow-automation.ts
 *
 * Async Decoupled Automation Rules Engine with Safety Governor & Template Evaluator.
 * Evaluates event triggers (onStatusChange, onPriorityChange, onAllChildrenDone, onDueDateApproaching),
 * executes multi-action cascades with max-depth safety limit (<= 3), and provides dry-run sandbox.
 */

import type { TaskFlowItem, TaskFlowPriority } from '../contracts/taskflow-schema.js';

export type AutomationTriggerType =
  | 'onStatusChange'
  | 'onPriorityChange'
  | 'onAllChildrenDone'
  | 'onDueDateApproaching';

export interface AutomationTrigger {
  type: AutomationTriggerType;
  toStatus?: string;
  fromStatus?: string;
  toPriority?: TaskFlowPriority;
  daysBeforeDue?: number;
}

export type AutomationActionType =
  | 'setStatus'
  | 'setPriority'
  | 'addTag'
  | 'removeTag'
  | 'setCompletedDate'
  | 'notifyChannel';

export interface AutomationAction {
  type: AutomationActionType;
  status?: string;
  priority?: TaskFlowPriority;
  tag?: string;
  value?: string;
  channelId?: string;
}

export interface AutomationRule {
  id: string;
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
}

export interface ItemMutation {
  itemId: string;
  patch: Partial<TaskFlowItem>;
  appliedRuleId: string;
}

export interface CascadeExecutionLog {
  depth: number;
  totalMutations: number;
  terminatedDueToSafetyGovernor: boolean;
  mutations: ItemMutation[];
}

export class TaskFlowAutomationEngine {
  private readonly rules = new Map<string, AutomationRule>();
  private static readonly MAX_CASCADE_DEPTH = 3;
  private static readonly MAX_MUTATIONS_BUDGET = 10;

  /**
   * Register or update an automation rule
   */
  registerRule(rule: AutomationRule): void {
    this.rules.set(rule.id, rule);
  }

  /**
   * Remove a rule by ID
   */
  removeRule(ruleId: string): void {
    this.rules.delete(ruleId);
  }

  /**
   * Get all registered rules
   */
  getRules(): AutomationRule[] {
    return Array.from(this.rules.values());
  }

  /**
   * Evaluates a single item mutation and calculates triggered rule actions
   */
  evaluateMutation(
    oldItem: TaskFlowItem,
    newItem: TaskFlowItem,
    allItems: TaskFlowItem[],
    actorId: string = 'system'
  ): ItemMutation[] {
    const mutations: ItemMutation[] = [];

    for (const rule of this.rules.values()) {
      if (!rule.enabled) continue;

      // 1. onStatusChange trigger
      if (
        rule.trigger.type === 'onStatusChange' &&
        oldItem.status !== newItem.status &&
        (!rule.trigger.toStatus || rule.trigger.toStatus === newItem.status)
      ) {
        const patch = this.executeActions(rule.actions, newItem, actorId);
        if (Object.keys(patch).length > 0) {
          mutations.push({ itemId: newItem.id, patch, appliedRuleId: rule.id });
        }
      }

      // 2. onPriorityChange trigger
      if (
        rule.trigger.type === 'onPriorityChange' &&
        oldItem.priority !== newItem.priority &&
        (!rule.trigger.toPriority || rule.trigger.toPriority === newItem.priority)
      ) {
        const patch = this.executeActions(rule.actions, newItem, actorId);
        if (Object.keys(patch).length > 0) {
          mutations.push({ itemId: newItem.id, patch, appliedRuleId: rule.id });
        }
      }

      // 3. onAllChildrenDone trigger
      if (rule.trigger.type === 'onAllChildrenDone' && newItem.parent) {
        const parentId = newItem.parent;
        const parent = allItems.find((i) => i.id === parentId);

        if (parent && parent.status !== 'done') {
          const siblings = allItems.filter(
            (i) =>
              i.parent === parentId &&
              i.id !== newItem.id &&
              i.status !== 'cancelled' &&
              i.status !== 'archived'
          );

          const allSiblingsDone = siblings.every((s) => s.status === 'done');
          const isCurrentDone = newItem.status === 'done';

          if (allSiblingsDone && isCurrentDone) {
            const parentPatch = this.executeActions(rule.actions, parent, actorId);
            if (Object.keys(parentPatch).length > 0) {
              mutations.push({ itemId: parentId, patch: parentPatch, appliedRuleId: rule.id });
            }
          }
        }
      }
    }

    return mutations;
  }

  /**
   * Executes multi-rule cascade with Safety Governor (max depth <= 3)
   */
  executeCascade(
    initialItem: TaskFlowItem,
    initialPatch: Partial<TaskFlowItem>,
    allItems: TaskFlowItem[],
    actorId: string = 'system'
  ): CascadeExecutionLog {
    let currentDepth = 0;
    let totalMutations = 0;
    let terminatedDueToSafetyGovernor = false;
    const recordedMutations: ItemMutation[] = [];

    let currentItem = { ...initialItem, ...initialPatch };
    let prevItem = initialItem;

    while (currentDepth < TaskFlowAutomationEngine.MAX_CASCADE_DEPTH) {
      currentDepth++;
      const nextMutations = this.evaluateMutation(prevItem, currentItem, allItems, actorId);

      if (nextMutations.length === 0) {
        break;
      }

      for (const mut of nextMutations) {
        totalMutations++;
        recordedMutations.push(mut);

        if (mut.itemId === currentItem.id) {
          prevItem = currentItem;
          currentItem = { ...currentItem, ...mut.patch };
        }
      }

      if (totalMutations >= TaskFlowAutomationEngine.MAX_MUTATIONS_BUDGET) {
        terminatedDueToSafetyGovernor = true;
        break;
      }
    }

    if (currentDepth >= TaskFlowAutomationEngine.MAX_CASCADE_DEPTH) {
      terminatedDueToSafetyGovernor = true;
    }

    return {
      depth: currentDepth,
      totalMutations,
      terminatedDueToSafetyGovernor,
      mutations: recordedMutations,
    };
  }

  /**
   * Dry-run sandbox evaluation without storage side-effects
   */
  dryRunRule(
    rule: AutomationRule,
    targetItem: TaskFlowItem,
    simulatedPatch: Partial<TaskFlowItem>
  ): { triggered: boolean; simulatedItem: TaskFlowItem } {
    const updated = { ...targetItem, ...simulatedPatch };
    const patch = this.executeActions(rule.actions, updated, 'sandbox');

    const triggered = Object.keys(patch).length > 0;
    return {
      triggered,
      simulatedItem: { ...updated, ...patch },
    };
  }

  /**
   * Evaluates template strings like "{{now + 3d}}", "{{item.title}}", "{{actor}}"
   */
  interpolateTemplate(
    template: string,
    ctx: { item: TaskFlowItem; actor?: string; nowMs?: number }
  ): string {
    const now = ctx.nowMs ? new Date(ctx.nowMs) : new Date();

    return template.replace(/\{\{([^}]+)\}\}/g, (_, expression: string) => {
      const expr = expression.trim();

      if (expr === 'now') {
        return now.toISOString().split('T')[0];
      }

      if (expr.startsWith('now +') || expr.startsWith('now -')) {
        const match = expr.match(/now\s*([+-])\s*(\d+)([dhw])/i);
        if (match) {
          const sign = match[1] === '+' ? 1 : -1;
          const val = parseInt(match[2], 10);
          const unit = match[3].toLowerCase();

          let msDelta = val * 24 * 60 * 60 * 1000;
          if (unit === 'h') msDelta = val * 60 * 60 * 1000;
          if (unit === 'w') msDelta = val * 7 * 24 * 60 * 60 * 1000;

          const target = new Date(now.getTime() + sign * msDelta);
          return target.toISOString().split('T')[0];
        }
      }

      if (expr === 'actor') {
        return ctx.actor || 'system';
      }

      if (expr.startsWith('item.')) {
        const field = expr.slice(5) as keyof TaskFlowItem;
        return String(ctx.item[field] ?? '');
      }

      return `{{${expr}}}`;
    });
  }

  private executeActions(
    actions: AutomationAction[],
    item: TaskFlowItem,
    actorId: string
  ): Partial<TaskFlowItem> {
    const patch: Partial<TaskFlowItem> = {};

    for (const act of actions) {
      if (act.type === 'setStatus' && act.status) {
        patch.status = act.status;
      } else if (act.type === 'setPriority' && act.priority) {
        patch.priority = act.priority;
      } else if (act.type === 'addTag' && act.tag) {
        const existingTags = patch.tags || item.tags || [];
        if (!existingTags.includes(act.tag)) {
          patch.tags = [...existingTags, act.tag];
        }
      } else if (act.type === 'removeTag' && act.tag) {
        const existingTags = patch.tags || item.tags || [];
        patch.tags = existingTags.filter((t) => t !== act.tag);
      } else if (act.type === 'setCompletedDate') {
        patch.completedDate = this.interpolateTemplate(act.value || '{{now}}', {
          item,
          actor: actorId,
        });
      }
    }

    return patch;
  }
}

export const globalAutomationEngine = new TaskFlowAutomationEngine();
